import { Injectable } from '@angular/core';
import { AccessLevel, ApplicationDataType, ComboControl, ControlType, DataContainer, GroupControl, IControlWithDefaultValue, iApplyFormControl, isControlWithObjectData } from './model';
import { deepClone } from './global.functions';
import { CUSTOM_DATEPICKER_FORMATS } from './datepicker-defaults';

@Injectable({
  providedIn: 'root'
})
export class InitialisationService {

  constructor() { }

  private noValueTypes = [ControlType.Heading, ControlType.Label, ControlType.Html, ControlType.Group];
  private arrayControlTypes = [ControlType.Repeater, ControlType.Checkboxgroup, ControlType.Filebrowser, ControlType.Photobooth];

  private repeaterCache = {};

  initControl(control: iApplyFormControl, container: DataContainer, getRepeaterMinRowCountFn) {
    let controlType = control.type,
      controlName = control.name,
      controlControls = (<GroupControl>control).controls;

    this.initContainerValue(container, control);

    if (controlControls && controlControls.length > 0) {
      if (controlType === ControlType.Repeater) {
        let controlMinimumCount = getRepeaterMinRowCountFn(control, container, container.$$qualifier || '');
        if (controlMinimumCount !== -1) {
          for (let j = 0; j < controlMinimumCount; j++) {
            if (container[controlName].length < controlMinimumCount) {
              this.initRepeaterChild(controlName, controlControls, container[controlName], getRepeaterMinRowCountFn);
            }
          }
        }
      } else {
        this.initContainer(controlControls, container, getRepeaterMinRowCountFn);
      }
    }
  }

  // eslint-disable-next-line complexity
  initContainerValue(container: DataContainer, control: iApplyFormControl) {
    // Only populate if there is no existing pre-populated value.
    if (!this.isControlValueEmpty(container, control)) {
      return;
    }

    let controlType = control.type;
    if (!this.noValueTypes.includes(controlType) && !container.hasOwnProperty(control.name)) {
      let controlValueArray = [],
        controlValue = (<IControlWithDefaultValue><unknown>control).defaultValue || '';

      if (controlType === ControlType.Checkboxgroup && controlValue) {
        controlValueArray.push(controlValue);
      }

      if (controlType === ControlType.Datepicker && controlValue) {
        let date = new Date().convertRelativeDateString(controlValue);
        controlValue = date.toFormattedString(CUSTOM_DATEPICKER_FORMATS.display.initialisationFormat);
      }

      let value = this.arrayControlTypes.includes(controlType) || (controlType === ControlType.Combo && (<ComboControl>control).allowMultipleSelect)
        ? controlValueArray
        : controlValue;

      if ((value === undefined || value === null || value === '') && isControlWithObjectData(control)) {
        value = deepClone(control.valueTemplate);
      }

      container[control.name] = value;
    }
  }

  isControlValueEmpty(container: DataContainer, control: iApplyFormControl) {
    if (!container || !container.hasOwnProperty(control.name)) {
      return true;
    }

    let value = container[control.name];

    if (Array.isArray(value) && value && value.length === 0) {
      return true;
    }

    if (typeof value == 'object' && value && value._type) {
      if (value._type === ApplicationDataType.ValueObject) {
        return value.value === '';
      }

      if (value._type === ApplicationDataType.ValueArray) {
        return value.values.length === 0;
      }
    }

    return (value === undefined || value === null || value === '');
  };

  initContainer(controls: iApplyFormControl[], application: DataContainer, getRepeaterMinRowCountFn) {
    for (let i = 0; i < controls.length; i++) {
      this.initControl(controls[i], application, getRepeaterMinRowCountFn);
    }
  };

  /**
   * @ngdoc method
   * @name ApplicationEditor.services:InitialisationService#initRepeaterChild
   * @methodOf ApplicationEditor.services:InitialisationService
   *
   * @description
   *
   * Instantiates a new data structure representing a new repeater row,
   * containing empty or default values allocated for every control in
   * that row.
   *
   * @returns {object} A new object representing the data structure of the
   * new repeater row.
   * @param {string} controlName the name of the repeater control
   * @param {object} controls A list of controls to instantiate within the
   * new repeater row.
   */
  initRepeaterChild(controlName: string, controls: iApplyFormControl[], container: DataContainer, getRepeaterMinRowCountFn): DataContainer {
    if (!this.repeaterCache[controlName]) {
      let template = {};
      for (let i = 0; i < controls.length; i++) {
        this.initControl(controls[i], template, getRepeaterMinRowCountFn);
      }

      this.repeaterCache[controlName] = template;
    }

    container.push(deepClone(this.repeaterCache[controlName]));
    return container[container.length - 1];
  };

  /**
   * @ngdoc method
   * @name ApplicationEditor.services:InitialisationService#initEntitlements
   * @methodOf ApplicationEditor.services:InitialisationService
   *
   * @description
   *
   * Iterates through a list of controls `controls` and first sets
   * the base entitlement `applicationEntitlement`, overwriting with
   * an entitlement for a given control in `map` if one exists.
   *
   * This also iterates down the control tree structure to set entitlements
   * recursively on nested controls.
   *
   * @param {object} controls A list of controls to iterate through and
   * instantiate.
   * @param {array} map A list of entitlement-to-control mappings.
   */
  initEntitlements(controls: iApplyFormControl[], map: any) {
    map = map || {};
    for (let i = 0; i < controls.length; i++) {
      let control = controls[i];
      control.access = !!map.W && map.W.indexOf(control.id) !== -1 ? AccessLevel.Write :
        !!map.R && map.R.indexOf(control.id) !== -1 ? AccessLevel.Read : AccessLevel.NoAccess;

      if ((<GroupControl>control).controls && (<GroupControl>control).controls.length > 0) {
        this.initEntitlements((<GroupControl>control).controls, map);
      }
    }
  };

  initExistingRepeaterRow(controls: iApplyFormControl[], container: DataContainer) {
    if (!container || (!Array.isArray(container) && typeof container != 'object')) {
      return container;
    }

    for (let i = 0; i < controls.length; i++) {
      let control = controls[i];

      if (control.type === ControlType.Group) {
        this.initExistingRepeaterRow((<GroupControl>control).controls, container);
        continue;
      }

      for (let j = 0; j < container.length; j++) {
        if (!container[j].hasOwnProperty(control.name)) {
          this.initContainerValue(container[j], control);
        }

        if (control.type === ControlType.Repeater) {
          this.initExistingRepeaterRow((<GroupControl>control).controls, container[j]);
        }
      }
    }

    return container;
  };
}
